import {Button, ContentSection, Icon, ListEmptySearch, SearchInput} from '@sollapay/ui/components';
import {ListExportMenu} from '@sollapay/web-core';
import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmActionModal} from '@/components';
import {showPayoutInstructionActionToast} from '@/features/payments/components';
import {
  useCancelPayoutInstructionMutation,
  usePayoutInstructionsQuery
} from '@/features/payments/hooks';
import {CreatePayoutFlow} from '@/features/payouts';
import {
  useCurrentTrustAccount,
  useTrustAccountPermissions
} from '@/features/trust-accounts/providers';

import {DEFAULT_PAYOUTS_FILTER_STATE} from './config/payouts-filter.config';
import {createPayoutExportColumns} from './payout-export.config';
import {PayoutsEmptyState} from './PayoutsEmptyState';
import {PayoutsFilter} from './PayoutsFilter';
import {PayoutsTabContentSkeleton} from './PayoutsTabContent.skeleton';
import {PayoutsTable} from './PayoutsTable';
import {buildSearchablePayouts, filterPayouts} from './utils/payouts-filter.utils';

import type {PayoutsFilterState} from './config/payouts-filter.config';
import type {PayoutInstructionDTO} from '@sollapay/types';
import type {FC} from 'react';

export const PayoutsTabContent: FC = () => {
  const {t, i18n} = useTranslation('trustAccounts');
  const {
    id: trustAccountId,
    refId: trustAccountRefId,
    name: trustAccountName
  } = useCurrentTrustAccount();
  const {canCreatePayout} = useTrustAccountPermissions();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<PayoutInstructionDTO | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<PayoutsFilterState>(DEFAULT_PAYOUTS_FILTER_STATE);

  const {data: payouts = [], isLoading} = usePayoutInstructionsQuery(trustAccountId);
  const cancelMutation = useCancelPayoutInstructionMutation(trustAccountId);

  const searchablePayouts = useMemo(() => buildSearchablePayouts(payouts), [payouts]);
  const filteredPayouts = useMemo(
    () => filterPayouts(searchablePayouts, filter, search),
    [searchablePayouts, filter, search]
  );
  const payoutExportColumns = useMemo(
    () => createPayoutExportColumns(t, i18n.language),
    [t, i18n.language]
  );

  const handleCreate = () => setIsCreateOpen(true);

  const handleConfirmCancel = () => {
    if (!cancelTarget) return;
    cancelMutation.mutate(cancelTarget.id, {
      onSuccess: () => {
        setCancelTarget(null);
        showPayoutInstructionActionToast('cancelSuccess');
      },
      onError: () => {
        setCancelTarget(null);
        showPayoutInstructionActionToast('cancelError');
      }
    });
  };

  if (isLoading) {
    return <PayoutsTabContentSkeleton />;
  }

  const isEmpty = payouts.length === 0;

  return (
    <>
      {isEmpty ? (
        <PayoutsEmptyState onCreate={canCreatePayout ? handleCreate : undefined} />
      ) : (
        <ContentSection
          title={t('view.payouts.title')}
          subtitle={t('view.payouts.subtitle')}
          action={
            canCreatePayout && (
              <Button
                variant="primary"
                size="sm"
                icon={<Icon name="add" width={16} height={16} />}
                onClick={handleCreate}
              >
                {t('view.payouts.list.newTransfer')}
              </Button>
            )
          }
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={t('view.payouts.list.search.placeholder')}
                className="w-80"
              />
              <div className="flex items-center gap-2">
                <ListExportMenu<PayoutInstructionDTO>
                  rows={filteredPayouts}
                  columns={payoutExportColumns}
                  refId={trustAccountRefId}
                  entity="payouts"
                  sheetName={trustAccountName}
                  disabled={filteredPayouts.length === 0}
                />
                <PayoutsFilter filter={filter} onChange={setFilter} />
              </div>
            </div>

            {filteredPayouts.length === 0 ? (
              <ListEmptySearch
                title={t('view.payouts.list.emptySearch.title')}
                subtitle={t('view.payouts.list.emptySearch.subtitle')}
                clearLabel={t('view.payouts.list.emptySearch.clear')}
                onClear={() => {
                  setSearch('');
                  setFilter(DEFAULT_PAYOUTS_FILTER_STATE);
                }}
              />
            ) : (
              <PayoutsTable
                payouts={filteredPayouts}
                onCancel={canCreatePayout ? setCancelTarget : undefined}
              />
            )}
          </div>
        </ContentSection>
      )}

      <CreatePayoutFlow
        trustAccountId={trustAccountId}
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <ConfirmActionModal
        isOpen={!!cancelTarget}
        icon="credit-card-cancel"
        iconVariant="destructive"
        primaryVariant="destructive"
        title={t('view.payouts.cancel.title')}
        description={t('view.payouts.cancel.description', {
          name: cancelTarget?.creditor.name ?? t('view.payouts.cancel.unknownBeneficiary')
        })}
        confirmLabel={t('view.payouts.cancel.confirmLabel')}
        isLoading={cancelMutation.isPending}
        onConfirm={handleConfirmCancel}
        onClose={() => setCancelTarget(null)}
      />
    </>
  );
};
